import { Icon } from './Icons';
import HealthAssessments from './HealthAssessments';
import Wearables from './Wearables';
import React, { useState, useEffect } from 'react';
import { loadAllBloodMarkers, loadLatestMetrics, loadMetricHistory } from '../db';
import { BIOMARKER_GROUPS, getStatus } from '../biomarkers';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip);

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function calcBMI(h, w) {
  if (!h || !w) return null;
  return (parseFloat(w) / Math.pow(parseFloat(h) / 100, 2)).toFixed(1);
}

const STATUS_COLOR = { ok: 'var(--success)', warn: 'var(--warning)', bad: 'var(--danger)', none: 'var(--text3)' };
const STATUS_DIM   = { ok: 'var(--success-dim)', warn: 'var(--warning-dim)', bad: 'var(--danger-dim)', none: 'var(--bg-elevated)' };
const STATUS_LABEL = { ok: 'Optimal', warn: 'Borderline', bad: 'Outside range', none: 'Not recorded' };

function MarkerRow({ groupKey, markerKey, value, marker }) {
  const status = getStatus(groupKey, markerKey, value);
  const color  = STATUS_COLOR[status];
  const rangeText = marker.optimal
    ? marker.optimal[0] + '–' + (marker.optimal[1] > 99 ? '∞' : marker.optimal[1])
    : '';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      padding: '.75rem 0',
      borderBottom: '1px solid var(--border2)',
    }}>
      <div>
        <div style={{ fontSize: '.82rem', color: 'var(--text)', fontWeight: 400 }}>{marker.label}</div>
        <div style={{ fontSize: '.67rem', color: 'var(--text3)', fontWeight: 300, marginTop: '.1rem' }}>
          {rangeText ? `Ref: ${rangeText} ${marker.unit}` : marker.unit}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {value ? (
          <div>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.15rem', fontWeight: 400, color, lineHeight: 1 }}>
              {value} <span style={{ fontSize: '.62rem', fontWeight: 300, color: 'var(--text3)' }}>{marker.unit}</span>
            </div>
            <span style={{ fontSize: '.6rem', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', padding: '.12rem .45rem', borderRadius: '4px', background: STATUS_DIM[status], color, display: 'inline-block', marginTop: '.2rem' }}>
              {STATUS_LABEL[status]}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>—</span>
        )}
      </div>
    </div>
  );
}

function GroupCard({ groupKey, group, bloodData }) {
  const [expanded, setExpanded] = useState(true);
  const entries = Object.entries(group.markers);
  const recorded = entries.filter(([k]) => bloodData[k]).length;
  const flagged  = entries.filter(([k]) => { const s = getStatus(groupKey, k, bloodData[k]); return s === 'warn' || s === 'bad'; }).length;
  const pct = Math.round(recorded / entries.length * 100);

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: '.85rem' }}>
      <button onClick={() => setExpanded(e => !e)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 1.25rem', background: 'transparent', border: 'none', cursor: 'pointer',
        borderBottom: expanded ? '1px solid var(--border2)' : 'none', transition: 'background .15s'
      }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flex: 1 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: group.color, flexShrink: 0, boxShadow: '0 0 8px ' + group.color + '55' }} />
          <span style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--text)' }}>{group.label}</span>
          {/* Progress bar */}
          <div style={{ flex: 1, maxWidth: 80, height: 3, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: flagged > 0 ? 'var(--warning)' : recorded > 0 ? 'var(--success)' : 'transparent', borderRadius: 2, transition: 'width .6s ease' }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexShrink: 0 }}>
          <span style={{ fontSize: '.65rem', padding: '.2rem .55rem', borderRadius: '20px', background: 'var(--bg-elevated)', color: 'var(--text3)', border: '1px solid var(--border2)' }}>
            {recorded}/{entries.length}
          </span>
          {flagged > 0 && (
            <span style={{ fontSize: '.65rem', padding: '.2rem .55rem', borderRadius: '20px', background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid rgba(240,160,80,.2)' }}>
              {flagged} flagged
            </span>
          )}
          <span style={{ fontSize: '.75rem', color: 'var(--text3)', marginLeft: '.25rem' }}>{expanded ? '↑' : '↓'}</span>
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '0 1.25rem' }}>
          {entries.map(([key, marker]) => (
            <MarkerRow key={key} groupKey={groupKey} markerKey={key} value={bloodData[key]} marker={marker} />
          ))}
          <div style={{ height: '.5rem' }} />
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, unit, status, note }) {
  const color = status ? STATUS_COLOR[status] : 'var(--text)';
  const dim = status ? STATUS_DIM[status] : 'var(--bg-card)';
  return (
    <div style={{ background: value && status ? dim : 'var(--bg-card)', border: '1px solid ' + (value && status && status !== 'none' ? color + '33' : 'var(--border2)'), borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
      <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: value ? color : 'var(--text3)', letterSpacing: '-.02em', lineHeight: 1 }}>{value || '—'}</div>
      {unit && <div style={{ fontSize: '.68rem', color: 'var(--text3)', marginTop: '.3rem', fontWeight: 300 }}>{unit}</div>}
      {value && status && status !== 'none' && <div style={{ fontSize: '.6rem', fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '.06em', marginTop: '.4rem', padding: '.15rem .5rem', background: dim, borderRadius: 10, display: 'inline-block' }}>{STATUS_LABEL[status]}</div>}
      {note && <div style={{ fontSize: '.67rem', color: 'var(--text3)', fontWeight: 300, marginTop: '.4rem', lineHeight: 1.5 }}>{note}</div>}
    </div>
  );
}

export default function MyMetrics({ initialTab = 0, onTabSet }) {
  const [tab, setTab] = useState(initialTab);

  React.useEffect(() => {
    if (initialTab > 0) { setTab(initialTab); if (onTabSet) onTabSet(); }
  }, [initialTab]);
  const [loading, setLoading] = useState(true);
  const [bloodData, setBloodData] = useState({});
  const [body, setBody] = useState({});
  const [mental, setMental] = useState({});
  const [fitness, setFitness] = useState({});
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [all, bo, m, f, gh] = await Promise.all([
          loadAllBloodMarkers(), loadLatestMetrics('body'),
          loadLatestMetrics('mental'), loadLatestMetrics('fitness'),
          loadMetricHistory('blood', 8)
        ]);
        if (all) setBloodData(all);
        if (bo)  setBody(bo);
        if (m)   setMental(m);
        if (f)   setFitness(f);
        if (gh)  setHistory(gh);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  const bmi = calcBMI(body.height, body.weight);
  const totalMarkers = Object.values(BIOMARKER_GROUPS).reduce((s, g) => s + Object.keys(g.markers).length, 0);
  const recorded = Object.keys(bloodData).filter(k => bloodData[k]).length;
  const flagged  = Object.entries(BIOMARKER_GROUPS).reduce((sum, [gk, g]) =>
    sum + Object.keys(g.markers).filter(mk => { const s = getStatus(gk, mk, bloodData[mk]); return s === 'warn' || s === 'bad'; }).length, 0);

  const TABS = [
    { label: 'Blood Results', icon: 'flask' },
    { label: 'Body', icon: 'scale' },
    { label: 'Mental Health', icon: 'brain' },
    { label: 'Fitness', icon: 'activity' },
    { label: 'Wearables', icon: 'watch' },
    { label: 'Assessments', icon: 'clipboard' }
  ];

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: 'var(--bg-card)', titleColor: 'var(--text)', bodyColor: 'var(--text3)', borderColor: 'var(--border2)', borderWidth: 1 } },
    scales: {
      x: { grid: { color: 'rgba(128,128,128,.1)', drawBorder: false }, ticks: { color: '#888', font: { size: 10, family: 'DM Sans' } } },
      y: { grid: { color: 'rgba(128,128,128,.1)', drawBorder: false }, ticks: { color: '#888', font: { size: 10, family: 'DM Sans' } }, min: 3, max: 9 }
    }
  };

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '180px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
      <div style={{ width: '280px', height: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>My Metrics</h1>
          <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>NHS/WHO reference ranges · {recorded} of {totalMarkers} markers recorded</p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {recorded > 0 && (
            <div style={{ padding: '.35rem .9rem', borderRadius: '20px', background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,.2)', fontSize: '.68rem', fontWeight: 600, color: 'var(--primary)' }}>
              {Math.round(recorded / totalMarkers * 100)}% complete
            </div>
          )}
          {flagged > 0 && (
            <div style={{ padding: '.35rem .9rem', borderRadius: '20px', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.2)', fontSize: '.68rem', fontWeight: 600, color: 'var(--warning)', letterSpacing: '.05em', textTransform: 'uppercase' }}>
              {flagged} flagged
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border2)', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {TABS.map((t, i) => (
          <button key={i} onClick={() => setTab(i)} style={{
            padding: '.6rem .75rem', background: 'transparent', border: 'none',
            color: tab === i ? 'var(--primary)' : 'var(--text3)',
            fontFamily: 'var(--font-body)', fontSize: '.78rem', cursor: 'pointer',
            borderBottom: tab === i ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-1px', fontWeight: tab === i ? 500 : 400,
            display: 'flex', alignItems: 'center', gap: '.35rem', transition: 'color .15s',
            whiteSpace: 'nowrap', flexShrink: 0
          }}>
            <Icon name={t.icon} size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Blood Results */}
      {tab === 0 && (
        <div>
          {recorded === 0 && (
            <div style={{ padding: '1rem 1.25rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius-lg)', marginBottom: '1rem', fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300 }}>
              No blood results recorded yet. Go to <strong style={{ color: 'var(--primary)', fontWeight: 500 }}>Upload Data</strong> to enter your results or upload a blood test PDF.
            </div>
          )}
          {Object.entries(BIOMARKER_GROUPS).map(([gk, group]) => (
            <GroupCard key={gk} groupKey={gk} group={group} bloodData={bloodData} />
          ))}
          {history.length > 1 && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginTop: '.5rem' }}>
              <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Glucose trend</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text3)', marginTop: '.15rem', fontWeight: 300 }}>{history.length} readings · optimal ≤5.5 mmol/L</div>
                </div>
                <div style={{ display: 'flex', gap: '.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.68rem', color: 'var(--text3)' }}><div style={{ width: 16, height: '2px', background: '#f0a050', borderRadius: 1 }} />Glucose</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.68rem', color: 'var(--text3)' }}><div style={{ width: 16, height: '1px', borderTop: '1px dashed rgba(220,38,38,.5)' }} />Threshold</div>
                </div>
              </div>
              <div style={{ padding: '1.25rem 1.5rem 1rem' }}>
                <div style={{ height: '160px' }}>
                  <Line data={{
                    labels: history.map(e => formatDate(e.recordedAt)),
                    datasets: [
                      { data: history.map(e => parseFloat(e.fastingGlucose) || null), borderColor: '#f0a050', backgroundColor: 'rgba(240,160,80,.05)', tension: .5, fill: true, pointRadius: 4, pointBackgroundColor: '#f0a050', borderWidth: 2 },
                      { data: history.map(() => 5.5), borderColor: 'rgba(220,38,38,.4)', borderDash: [4,4], pointRadius: 0, borderWidth: 1 }
                    ]
                  }} options={chartOpts} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Body Metrics */}
      {tab === 1 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.85rem', marginBottom: '1rem' }}>
            <StatBox label="Height" value={body.height} unit="cm" />
            <StatBox label="Weight" value={body.weight} unit="kg" />
            <StatBox label="BMI" value={bmi} unit="kg/m²" status={bmi ? (parseFloat(bmi)<25?'ok':parseFloat(bmi)<30?'warn':'bad') : 'none'} note={bmi ? (parseFloat(bmi)<18.5?'Underweight':parseFloat(bmi)<25?'Healthy range':parseFloat(bmi)<30?'Overweight':'Obese') : null} />
            <StatBox label="Waist" value={body.waist} unit="cm" status={body.waist?(parseFloat(body.waist)<80?'ok':parseFloat(body.waist)<94?'warn':'bad'):'none'} />
          </div>
          <div style={{ background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Reference ranges</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.5rem', fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              <div>BMI 18.5–24.9 = healthy · 25–29.9 = overweight · 30+ = obese</div>
              <div>Waist below 80cm (women) or 94cm (men) = low metabolic risk</div>
            </div>
          </div>
        </div>
      )}

      {/* Mental Health */}
      {tab === 2 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.85rem', marginBottom: '1rem' }}>
            <StatBox label="Stress Score" value={mental.stressScore} unit="/100" status={mental.stressScore?(parseFloat(mental.stressScore)<40?'ok':parseFloat(mental.stressScore)<65?'warn':'bad'):null} note="Below 40 = low risk · 40–65 = moderate · 65+ = high" />
            <StatBox label="Burnout Score" value={mental.burnoutScore} unit="/100" status={mental.burnoutScore?(parseFloat(mental.burnoutScore)<40?'ok':parseFloat(mental.burnoutScore)<60?'warn':'bad'):null} />
            <StatBox label="Sleep" value={mental.sleepHours} unit="hrs/night" status={mental.sleepHours?(parseFloat(mental.sleepHours)>=7?'ok':parseFloat(mental.sleepHours)>=6?'warn':'bad'):null} note="7–9 hours is optimal for adults" />
            <StatBox label="PHQ-9" value={mental.phq9} unit="/27" status={mental.phq9?(parseFloat(mental.phq9)<5?'ok':parseFloat(mental.phq9)<10?'warn':'bad'):null} note="Depression screening score" />
            <StatBox label="GAD-7" value={mental.gad7} unit="/21" status={mental.gad7?(parseFloat(mental.gad7)<5?'ok':parseFloat(mental.gad7)<10?'warn':'bad'):null} note="Anxiety screening score" />
          </div>
          <div style={{ background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Why mental health affects your blood results</div>
            <p style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              Chronic stress raises cortisol, which directly elevates fasting glucose, increases LDL cholesterol, raises blood pressure and suppresses immune function — all measurable in standard blood tests.
            </p>
          </div>
        </div>
      )}

      {/* Fitness */}
      {tab === 4 && <Wearables />}
      {tab === 5 && <HealthAssessments />}
      {tab === 3 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.85rem', marginBottom: '1rem' }}>
            <StatBox label="Steps / Day" value={fitness.stepsPerDay} unit="avg daily" status={fitness.stepsPerDay?(parseFloat(fitness.stepsPerDay)>=8000?'ok':parseFloat(fitness.stepsPerDay)>=5000?'warn':'bad'):null} note="Target: 8,000–10,000/day" />
            <StatBox label="Workouts / Week" value={fitness.workoutsPerWeek} unit="sessions" status={fitness.workoutsPerWeek?(parseFloat(fitness.workoutsPerWeek)>=3?'ok':parseFloat(fitness.workoutsPerWeek)>=1?'warn':'bad'):null} />
            <StatBox label="Resting HR" value={fitness.restingHR} unit="bpm" status={fitness.restingHR?(parseFloat(fitness.restingHR)<70?'ok':parseFloat(fitness.restingHR)<85?'warn':'bad'):null} note="Below 70 bpm = good cardiovascular fitness" />
            <StatBox label="Active Minutes" value={fitness.activeMinutes} unit="per day" status={fitness.activeMinutes?(parseFloat(fitness.activeMinutes)>=30?'ok':parseFloat(fitness.activeMinutes)>=15?'warn':'bad'):null} note="Target: 30+ min/day" />
          </div>
          <div style={{ background: 'rgba(0,200,150,0.04)', border: '1px solid rgba(0,200,150,0.15)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>WHO activity guidelines</div>
            <p style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              150 minutes of moderate activity per week improves glucose control by up to 30%, reduces LDL cholesterol by 10% and lowers resting blood pressure by 5–8 mmHg on average.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
